/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { Fragment, type FC, type ReactNode } from 'react';

import classNames from 'classnames';

import { reportNavClick } from '../utils';
import { type LayoutButtonItem, type LayoutMenuItem } from '../types';

interface GlobalLayoutMergedFooterProps {
  menus?: LayoutMenuItem[];
  extras?: LayoutButtonItem[];
  footer?: ReactNode;
  standalone?: boolean;
}

const itemStyle = classNames(
  'flex items-center gap-[8px]',
  'h-[32px] w-full px-[8px]',
  'rounded-[8px]',
  'transition-colors',
  'cursor-pointer',
  'text-[14px] leading-[20px] font-[500]',
);

const iconStyle = 'w-[16px] h-[16px] text-[16px] leading-none';

const getActiveState = ({
  path,
  pathname,
}: {
  path: string | string[];
  pathname: string;
}) => {
  if (Array.isArray(path)) {
    return {
      isActive: path.some(item => pathname.startsWith(item)),
      navPath: path.find(item => pathname.startsWith(item)) || path[0],
    };
  }

  return {
    isActive: pathname.startsWith(path),
    navPath: path,
  };
};

const FooterMenuItem: FC<LayoutMenuItem> = ({
  title,
  icon,
  activeIcon,
  path,
  dataTestId,
}) => {
  const location = useLocation();
  const { isActive, navPath } = getActiveState({
    path,
    pathname: location.pathname,
  });
  const isLink = navPath.startsWith('https://');

  return (
    <NavLink
      to={navPath}
      target={isLink ? '_blank' : undefined}
      className="no-underline"
      onClick={() => {
        reportNavClick(title);
      }}
      data-testid={dataTestId}
    >
      <div
        className={classNames(
          itemStyle,
          isActive
            ? 'coz-bg-primary coz-fg-plus'
            : 'coz-fg-primary hover:coz-mg-secondary-hovered',
        )}
      >
        <div className={iconStyle}>{isActive ? activeIcon : icon}</div>
        <span className="flex-1 truncate">{title}</span>
      </div>
    </NavLink>
  );
};

const FooterActionItem: FC<LayoutButtonItem> = ({
  icon,
  tooltip,
  onClick,
  dataTestId,
  portal,
}) => (
  <Fragment>
    <button
      type="button"
      className={classNames(
        itemStyle,
        'border-0 bg-transparent text-left',
        'coz-fg-primary hover:coz-mg-secondary-hovered',
      )}
      onClick={() => {
        reportNavClick(tooltip);
        onClick?.();
      }}
      data-testid={dataTestId}
    >
      <div className={iconStyle}>{icon}</div>
      <span className="flex-1 truncate">{tooltip}</span>
    </button>
    {portal}
  </Fragment>
);

export const GlobalLayoutMergedFooter: FC<GlobalLayoutMergedFooterProps> = ({
  menus,
  extras,
  footer,
  standalone = false,
}) => (
  <div
    className={classNames(
      standalone
        ? null
        : 'pt-[12px] mt-[12px] border-0 border-t-[1px] border-solid coz-stroke-primary',
    )}
  >
    <div className="flex flex-col gap-[4px]">
      {menus?.map((menu, index) => <FooterMenuItem {...menu} key={index} />)}
      {extras?.map((extra, index) => (
        <FooterActionItem {...extra} key={index} />
      ))}
    </div>
    {footer ? <div className="mt-[8px]">{footer}</div> : null}
  </div>
);
